import { useEffect, useState, useMemo } from "react";
import { default as ReactSelect } from "react-select";
import CreatableSelect from "react-select/creatable";
import { Box, FormControl } from "native-base";
import InputErrorMessage from "../inputErrorMessage";
import "./style.css";

const SearchableSelectDropdown = ({
  label = "",
  name = "searchdrop",
  selectedValues = "",
  defaultValue = "",
  setSelectedValues = () => {},
  placeholder = "",
  dropItems = [],
  width = "auto",
  isDisabled = false,
  isInvalid = false,
  errorMessage = "Select option",
  important = false,
  forceRemount = false,
  isMulti = false, // New prop to support single-select mode
  allowCustomValue = false,
  isClearable = false,
}) => {
  const [selectedOptions, setSelectedOptions] = useState(isMulti ? [] : null);
  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Convert dropItems to react-select format if needed
  const options = useMemo(() => {
    return dropItems.map((item) => {
      // If item already has value and label, use it as is
      if (item.value !== undefined && item.label !== undefined) {
        return item;
      }
      // Otherwise, create from item properties
      return {
        value: item.value || item.id || item,
        label: item.label || item.name || item.value || item.id || item,
        ...item, // Preserve other properties
      };
    });
  }, [dropItems]);

  useEffect(() => {
    if (isMulti) {
      setSelectedOptions(selectedValues || []);
    } else {
      // Single select mode
      if (selectedValues) {
        const found = options.find(
          (opt) => opt.value === selectedValues || opt.value?.toString() === selectedValues?.toString()
        );
        if (found) {
          setSelectedOptions(found);
        } else if (allowCustomValue) {
          setSelectedOptions({
            value: selectedValues,
            label: selectedValues,
            __isCustomValue: true,
          });
        } else {
          setSelectedOptions(null);
        }
      } else {
        setSelectedOptions(null);
      }
    }
    setInputValue("");
  }, [selectedValues, options, isMulti, allowCustomValue]);

  const handleChange = (value) => {
    setSelectedOptions(value);
    setInputValue("");
    if (isMulti) {
      // Multi-select mode - maintain backward compatibility
      if (value && value.length > 0 && value[0]?.id !== undefined) {
        setSelectedValues(value[0]["id"], name);
      } else {
        setSelectedValues(value, name);
      }
    } else {
      // Single-select mode
      if (value) {
        setSelectedValues(value.value, name);
      } else {
        setSelectedValues("", name);
      }
    }
  };

  const handleCreateOption = (inputValue) => {
    const normalizedInput = inputValue?.trim();
    if (!normalizedInput) return;

    const createdOption = {
      value: normalizedInput,
      label: normalizedInput,
      __isCustomValue: true,
    };

    setSelectedOptions(createdOption);
    setInputValue("");
    setSelectedValues(normalizedInput, name);
  };

  const SelectComponent = allowCustomValue && !isMulti ? CreatableSelect : ReactSelect;

  const handleBlur = () => {
    setFocused(false);
    if (!allowCustomValue || isMulti) return;
    const normalizedInput = inputValue?.trim();
    if (!normalizedInput) return;
    handleCreateOption(normalizedInput);
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "36px",
      height: "36px",
      fontSize: "14px",
      borderColor: isInvalid ? "#E53E3E" : state.isFocused ? "#3182CE" : "#E2E8F0",
      boxShadow: state.isFocused ? "0 0 0 1px #3182CE" : "none",
      "&:hover": {
        borderColor: isInvalid ? "#E53E3E" : "#3182CE",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "36px",
      padding: "0 8px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
      // color: isDisabled ? "#000" : provided.color,
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "36px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      "& svg": {
        fill: "rgb(142, 139, 139)",
        width: "25px",
        height: "16px",
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      "& svg": {
        fill: "rgb(142, 139, 139)",
        width: "15px",
        height: "16px",
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      minWidth: "400px",
      width: "max-content",
      maxWidth: "90vw",
      backgroundColor: "#f5f5f5",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "300px",
      padding: "4px 0",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3182CE"
        : state.isFocused
        ? "#e0e0e0"
        : "#f5f5f5",
      color: state.isSelected ? "white" : "inherit",
      padding: "8px 12px",
      cursor: "pointer",
      "&:active": {
        backgroundColor: state.isSelected ? "#3182CE" : "#d0d0d0",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDisabled ? "#666" : provided.color,
    }),
    // placeholder: (provided) => ({
    //   ...provided,
    //   color: isDisabled ? "#000" : provided.color,
    // }),
  };

  const formControlKey = useMemo(() => Date.now(), [forceRemount]);
  const resolvedPlaceholder = allowCustomValue
    ? `Search or Enter ${placeholder}`
    : focused
    ? `Search ${placeholder}`
    : `Select ${placeholder}`;

  return (
    <Box width={width}>
      <FormControl isInvalid={isInvalid} isDisabled={isDisabled} w={width} key={formControlKey}>
        {label && (
          <FormControl.Label>
            {label} {important && <span> *</span>}
          </FormControl.Label>
        )}
        <SelectComponent
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          options={options}
          value={selectedOptions}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          onInputChange={(value, actionMeta) => {
            if (actionMeta?.action === "input-change") {
              setInputValue(value);
            } else if (actionMeta?.action === "menu-close" || actionMeta?.action === "set-value") {
              setInputValue("");
            }
          }}
          defaultValue={defaultValue}
          className={isInvalid ? "invalid-dropdown" : "multiselect-dropdown"}
          placeholder={resolvedPlaceholder}
          styles={customStyles}
          isSearchable={true}
          isClearable={isClearable}
          isMulti={isMulti}
          openMenuOnFocus={!allowCustomValue}
          noOptionsMessage={({ inputValue: currentInput }) => {
            if (allowCustomValue) return null;
            return "No options";
          }}
          filterOption={(option, searchText) => {
            if (!searchText) return true;
            const searchLower = searchText.toLowerCase();
            const label = option.label?.toLowerCase() || "";
            const value = option.value?.toString().toLowerCase() || "";
            return label.includes(searchLower) || value.includes(searchLower);
          }}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          onCreateOption={allowCustomValue && !isMulti ? handleCreateOption : undefined}
          formatCreateLabel={allowCustomValue && !isMulti ? ((inputValue) => `Use "${inputValue}"`) : undefined}
        />
        {isInvalid && <InputErrorMessage errorMessage={errorMessage} />}
      </FormControl>
    </Box>
  );
};

export default SearchableSelectDropdown;
